import { FetchError } from '../errors/FetchError'
import { fetchWithAuthorization } from './fetch'
import { TOO_MANY_REQUESTS_MESSAGE, isTooManyRequests } from '../errors/tooManyRequests'
import { mockRewardsSummary, mockRewardsTransactions } from '../../components/rewards/mocks/rewardsMock'
import { mockRedeemCoins, mockRedemptionLimits } from '../../components/rewards/mocks/redemptionMock'
import { mockReferrals } from '../../components/rewards/mocks/referralsMock'
import { mockCondominiumRewardsSummary } from '../../components/rewards/mocks/condominiumRewardsMock'
import {
  mockMarkNotificationRead,
  mockNotificationSettings,
  mockRewardsNotifications,
  mockUpdateNotificationSettings
} from '../../components/rewards/mocks/notificationsMock'

// HO-TASK-282: contrato real de la API de Rewards, documentado en
// docs/rewards-api.md del backend (rama HO-TASK-275-rewards-api). Mientras esa
// rama no esté desplegada, en desarrollo estas funciones responden con datos
// simulados con la misma forma (ver rewardsMock.js). En un build de producción
// nunca se usan los datos simulados: ahí siempre se llama a la API real.
//
// Para probar contra el backend en local: VITE_REWARDS_MOCK=false en el .env.
// Cuando HO-TASK-275 esté desplegado, borrar este switch y el archivo de mocks.
const USE_REWARDS_MOCK = import.meta.env.DEV && import.meta.env.VITE_REWARDS_MOCK !== 'false'

export const getRewardsSummary = async () => {
  if (USE_REWARDS_MOCK) return mockRewardsSummary()

  try {
    const data = await fetchWithAuthorization('/rewards/me')
    return data
  } catch (error) {
    throw new FetchError(error, 'Ha ocurrido un error al consultar tu resumen de Homelan Rewards.')
  }
}

export const getRewardsTransactions = async (page = 1, limit = 10) => {
  if (USE_REWARDS_MOCK) return mockRewardsTransactions(page, limit)

  try {
    const data = await fetchWithAuthorization(`/rewards/transactions?page=${page}&limit=${limit}`)
    return data
  } catch (error) {
    throw new FetchError(error, 'Ha ocurrido un error al consultar tu historial de Homelan Rewards.')
  }
}

// HO-TASK-285: estos dos endpoints ya siguen el contrato REAL de la API
// (docs/rewards-api.md del backend, rama HO-TASK-275-rewards-api), no uno
// propuesto: GET /rewards/redemptions/limits y POST /rewards/redeem.
export const getRedemptionLimits = async () => {
  if (USE_REWARDS_MOCK) return mockRedemptionLimits()

  try {
    const data = await fetchWithAuthorization('/rewards/redemptions/limits')
    return data
  } catch (error) {
    throw new FetchError(error, 'Ha ocurrido un error al consultar tus Homelan Coins disponibles.')
  }
}

// `idempotencyKey` debe ser el mismo para todos los reintentos de un mismo
// canje: el backend la usa para devolver el canje original en vez de gastar
// las monedas dos veces.
export const redeemCoins = async ({ hcAmount, idempotencyKey }) => {
  const body = { hc_amount: hcAmount, idempotency_key: idempotencyKey }
  if (USE_REWARDS_MOCK) return mockRedeemCoins(body)

  try {
    const data = await fetchWithAuthorization('/rewards/redeem', body, 'POST')
    return data
  } catch (error) {
    throw new FetchError(error, 'No pudimos completar el canje de tus Homelan Coins.')
  }
}

// HO-TASK-286: código de referido y estado de cada referido. Contrato real:
// GET /rewards/referrals (RewardReferralController del backend).
export const getRewardsReferrals = async () => {
  if (USE_REWARDS_MOCK) return mockReferrals()

  try {
    const data = await fetchWithAuthorization('/rewards/referrals')
    return data
  } catch (error) {
    throw new FetchError(error, 'Ha ocurrido un error al consultar tus referidos.')
  }
}

// HO-TASK-288: resumen agregado del programa en un condominio, para el
// administrador. OJO: este endpoint NO existe todavía en el backend; es un
// contrato propuesto (ver condominiumRewardsMock.js).
export const getCondominiumRewardsSummary = async (condominiumId, months = 6) => {
  if (USE_REWARDS_MOCK) return mockCondominiumRewardsSummary(condominiumId, months)

  try {
    const data = await fetchWithAuthorization(`/rewards/condominiums/${condominiumId}/summary?months=${months}`)
    return data
  } catch (error) {
    throw new FetchError(error, 'Ha ocurrido un error al consultar el resumen de Homelan Rewards del condominio.')
  }
}

// HO-TASK-290: notificaciones del programa (HO-TASK-278). Son las mismas de la
// campana (GET /notifications), filtradas por un tipo `rewards-*`.
export const getRewardsNotifications = async (type, limit = 10) => {
  if (USE_REWARDS_MOCK) return mockRewardsNotifications(type, limit)

  try {
    const data = await fetchWithAuthorization(`/notifications?type=${encodeURIComponent(type)}&limit=${limit}`)
    return data
  } catch (error) {
    throw new FetchError(error, 'Ha ocurrido un error al consultar tus avisos de Homelan Rewards.')
  }
}

export const markRewardsNotificationsRead = async (ids) => {
  // Mismo patrón que el resto (return temprano): así el build elimina el mock.
  if (USE_REWARDS_MOCK) return Promise.all(ids.map(mockMarkNotificationRead))

  try {
    return await Promise.all(ids.map((id) => fetchWithAuthorization(`/notifications/${id}`, null, 'PUT')))
  } catch (error) {
    throw new FetchError(error, 'No pudimos marcar tus avisos como leídos.')
  }
}

// La preferencia vive en la wallet de la unidad activa: silenciar Rewards no
// toca las notificaciones del condominio.
export const getRewardsNotificationSettings = async () => {
  if (USE_REWARDS_MOCK) return mockNotificationSettings()

  try {
    const data = await fetchWithAuthorization('/rewards/notifications/settings')
    return data
  } catch (error) {
    throw new FetchError(error, 'Ha ocurrido un error al consultar tus preferencias de Homelan Rewards.')
  }
}

export const updateRewardsNotificationSettings = async (enabled) => {
  const body = { notifications_enabled: enabled }
  if (USE_REWARDS_MOCK) return mockUpdateNotificationSettings(enabled)

  try {
    const data = await fetchWithAuthorization('/rewards/notifications/settings', body, 'PUT')
    return data
  } catch (error) {
    throw new FetchError(error, 'No pudimos guardar tu preferencia de avisos de Homelan Rewards.')
  }
}

// Mensaje para mostrar cuando falla un canje. Las reglas de negocio (saldo
// insuficiente, mínimo, tope, wallet bloqueada, fondo sin cobertura) llegan
// como 422 con `error` ya en español; la validación como 406 con `errors`.
export const getRedeemErrorMessage = (error) => {
  const data = error?.data
  if (data?.error) return data.error

  const fieldErrors = data?.errors && Object.values(data.errors).flat()
  if (fieldErrors?.length) return fieldErrors[0]

  // HO-TASK-297: fetch.js ya convierte cualquier 429 en H0044, incluso el
  // "Too Many Attempts." que devolvía el backend antes de HO-TASK-295.
  if (isTooManyRequests(data)) return data.message || TOO_MANY_REQUESTS_MESSAGE

  return error?.message || 'No pudimos completar el canje de tus Homelan Coins. Intenta de nuevo.'
}
